import { useState } from "react";
import type { PlayerStanding, SubmissionView, GameItem } from "@/types/api";
import { selectPhotos, loadImages, generateCollage } from "@/utils/collage";
import Button from "@/components/ui/Button";
import { Share2, Download } from "lucide-react";

interface ShareRecapProps {
  gameTitle: string;
  standings: PlayerStanding[];
  submissions: SubmissionView[];
  items: GameItem[];
  playerNames: Record<string, string>;
  currentPlayerName: string;
}

export default function ShareRecap({
  gameTitle,
  standings,
  submissions,
  items,
  playerNames,
  currentPlayerName,
}: ShareRecapProps) {
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canShare = typeof navigator.share === "function";

  const buildCollageBlob = async (): Promise<Blob> => {
    const photos = selectPhotos(submissions, items, playerNames);
    const urls = photos.map((p) => p.submission.imageUrl);
    const images = await loadImages(urls);
    return generateCollage({ gameTitle, standings, photos, images });
  };

  const getShareText = (): { title: string; text: string } => {
    const sorted = [...standings].sort((a, b) => b.totalScore - a.totalScore);
    const isWinner = sorted.length > 0 && sorted[0]!.displayName === currentPlayerName;
    const myStanding = standings.find((s) => s.displayName === currentPlayerName);
    const score = myStanding
      ? `${myStanding.approvedCount}/${myStanding.totalItems}`
      : "";

    const title = `${gameTitle} - ISPYFR`;
    const text = isWinner
      ? `I won ${gameTitle} on ISPYFR! ${score} items found`
      : `Just played ${gameTitle} on ISPYFR! ${score} items found`;

    return { title, text };
  };

  const handleShare = async () => {
    setGenerating(true);
    setError(null);
    try {
      const blob = await buildCollageBlob();
      const file = new File([blob], "ispyfr-recap.jpg", { type: "image/jpeg" });
      const { title, text } = getShareText();
      const shareUrl = "https://ispyfr.com";

      // Try sharing with file
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title, text, url: shareUrl });
      } else {
        // Fallback: share without file
        await navigator.share({ title, text, url: shareUrl });
      }
    } catch (err) {
      // AbortError = user cancelled share sheet, not an error
      if (err instanceof Error && err.name === "AbortError") return;
      setError("Failed to share. Try downloading instead.");
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    setGenerating(true);
    setError(null);
    try {
      const blob = await buildCollageBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ispyfr-recap.jpg";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setError("Failed to generate recap image.");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="mt-4 rounded-xl border border-border bg-surface p-4">
      <p className="text-center text-sm font-semibold text-text-secondary uppercase tracking-wide">
        Game Recap
      </p>
      <div className="mt-3 flex gap-2">
        {canShare ? (
          <Button
            variant="primary"
            onClick={handleShare}
            loading={generating}
            className="flex-1"
          >
            <Share2 className="h-4 w-4" />
            Share Recap
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={handleDownload}
            loading={generating}
            className="flex-1"
          >
            <Download className="h-4 w-4" />
            Download Recap
          </Button>
        )}
        {canShare && (
          <Button
            variant="ghost"
            onClick={handleDownload}
            loading={generating}
          >
            <Download className="h-4 w-4" />
          </Button>
        )}
      </div>
      {error && (
        <p className="mt-2 text-center text-sm text-error">{error}</p>
      )}
    </div>
  );
}
